import { createSelector, createStructuredSelector } from 'reselect';
import {
  pipe,
  orderBy,
  reject,
  filter,
  overEvery,
  overSome,
  negate,
  isEmpty,
  some,
  get,
  lowerCase,
} from 'lodash/fp';
import { allAssignmentsSelector } from './assignments';
import * as topicsSelectors from './topics';
import {
  isAssignedDirectly,
  isInCollection,
  isCompleted,
  isStarted,
  isAvailableDirectly,
  isFeatured,
} from './content';
import { isJourneyStarted } from './journeyContent';

const contentsSelector = state => state.data.contents;
const journeyContentsSelector = state => state.data.journeyContents;

const lowerCaseTitle = pipe(get('title'), lowerCase);
const orderByTitle = orderBy(lowerCaseTitle, 'asc');

/**
 * @returns contents that are not in a topic and not an incomplete assignment
 * in an alphabetical order
 */
export const additionalContentsSelector = createSelector(
  [contentsSelector, topicsSelectors.allTopicsContents],
  (contents, allTopicsContents) =>
    pipe(
      reject(
        overSome([
          negate(isAvailableDirectly),
          overEvery([isAssignedDirectly, negate(isCompleted)]),
          isInCollection(allTopicsContents),
        ]),
      ),
      orderByTitle,
    )(contents),
);

export const featuredContentsSelector = createSelector(
  contentsSelector,
  pipe(filter(isFeatured), orderByTitle),
);

export const hasFeaturedContents = createSelector(
  featuredContentsSelector,
  negate(isEmpty),
);

const libraryContentsSelector = createSelector(
  contentsSelector,
  reject(overSome([negate(isAvailableDirectly), isAssignedDirectly])),
);

export const hasLibraryContents = createSelector(
  libraryContentsSelector,
  negate(isEmpty),
);

export const hasStartedAny = createSelector(
  contentsSelector,
  some('progress.startDate'),
);

export const hasStartedAnyJourney = createSelector(
  journeyContentsSelector,
  some('progress.startedAt'),
);

export const historySelector = createSelector(
  contentsSelector,
  filter(overEvery([isAvailableDirectly, isStarted])),
);

export const historyJourneySelector = createSelector(
  journeyContentsSelector,
  filter(isJourneyStarted),
);

export const startedContents = createSelector(
  contentsSelector,
  filter(isStarted),
);

export const completedContents = createSelector(
  contentsSelector,
  filter(isCompleted),
);

export const contentsByProgressSelector = createStructuredSelector({
  assigned: allAssignmentsSelector,
  completed: completedContents,
  started: startedContents,
});
